import { useEffect, useState } from 'react';
import { SliderField } from './SliderField';
import { useEditor, useNode } from '@craftjs/core';
import { useBreakpointAwareProp } from '@/hooks/useBreakpointAwareProp';
import { percentToPx, getElementDimensionsWithoutPadding } from '@/utils/numToMeasurement';
import type { TextProps } from '@builder4/shared/components/Text';
import type { ContainerProps } from '@builder4/shared/components/Container';
import type { ImageProps } from '@builder4/shared/components/Image';
import type { FormProps } from '@builder4/shared/components/Form';

type PropsWithMaxWidth = Partial<TextProps | ContainerProps | ImageProps | FormProps>;

const NodeMaxWidthSettings = () => {
  const { nodeProps, id, displayName, node } = useNode((node) => ({
    id: node.id,
    nodeProps: node.data.props,
    displayName: node.data.displayName,
    node: node as typeof node,
  }));
  const { actions } = useEditor();
  const { getBPProp, setBPProp } = useBreakpointAwareProp(displayName);
  const maxWidth = getBPProp(nodeProps, 'maxWidth') ?? 100;
  const widthUnit = getBPProp(nodeProps, 'maxWidthUnit');
  // For display, default to 'px' if not set (new components), but check actual value for conversion
  const [localMaxWidth, setLocalMaxWidth] = useState(maxWidth);

  useEffect(() => {
    setLocalMaxWidth(maxWidth);
  }, [maxWidth]);
  const dom = node?.dom as HTMLElement | null;

  useEffect(() => {
    const actualUnit = widthUnit;
    if (actualUnit === '%' && maxWidth) {
      if (!dom) return;

      const parentElement = dom.parentElement;
      if (!parentElement) return;

      const parentDimensions = getElementDimensionsWithoutPadding(parentElement);

      const pixelString = percentToPx(`${maxWidth}%`, parentDimensions.width);

      const pixelNumber = parseFloat(pixelString);
      if (!isNaN(pixelNumber) && pixelNumber > 0) {
        const roundedPixel = Math.round(pixelNumber);
        setLocalMaxWidth(roundedPixel);
      }
    }
  }, [dom, id, widthUnit, maxWidth]); // Run when node changes (id changes when different node is selected)

  const handleOnChange = (value: number) => {
    setLocalMaxWidth(value);
    actions.history.ignore().setProp(id, (props: PropsWithMaxWidth) => {
      setBPProp(props, 'maxWidth', value);
      // Set maxWidthUnit to 'px' when user changes maxWidth
      setBPProp(props, 'maxWidthUnit', 'px');
    });
  };

  const handleOnChangeComplete = (value: number, initialValue: number) => {
    actions.history.ignore().setProp(id, (props: PropsWithMaxWidth) => {
      setBPProp(props, 'maxWidth', initialValue);
    });
    setLocalMaxWidth(value);
    actions.setProp(id, (props: PropsWithMaxWidth) => {
      setBPProp(props, 'maxWidth', value);
      // Set maxWidthUnit to 'px' when user changes maxWidth
      setBPProp(props, 'maxWidthUnit', 'px');
    });
  };

  return (
    <SliderField
      label="Max Width"
      value={localMaxWidth}
      onChange={handleOnChange}
      onChangeComplete={handleOnChangeComplete}
      min={1}
      max={1200}
      suffix={'px'}
    />
  );
};

export default NodeMaxWidthSettings;
